"use client";
import {
  Tag,
  Bot,
  Pointer,
  LucideIcon,
} from "lucide-react";
import { useRef } from "react";
import PageContent from '@/components/page-content';
import { AspectRatio } from "@/components/ui/aspect-ratio";
import { Button } from "@/components/ui/button";
import SectionHeader from '@/components/landing/section-header';
import { CircleArrowRight } from "lucide-react";

interface card {
  video: string;
  Icon: LucideIcon;
  title: string;
  link: string;
  summary: string;
}

const List: Array<card> = [
  {
    video: "https://depot.moondream.ai/tagging_960x540.mp4",
    Icon: Tag,
    title: "Generate Media Tags",
    link: "/solutions/generate-media-tags",
    summary:
      "Make every image and video searchable. Automatically generate tags, extract metadata, and find exactly what you need across millions of files, no manual labeling required."
  },
  {
    video: "https://depot.moondream.ai/robotics_960x540.mp4",
    Icon: Bot,
    title: "Smart Robotics",
    link: "/solutions/robot-vision",
    summary:
      "Give robots the gift of sight. Natural language prompts like 'Find the red ball' or 'Is the path clear?' enable flexible, adaptive behavior without retraining.",
  },
  {
    video: "https://depot.moondream.ai/ui_960x540.mp4",
    Icon: Pointer,
    title: "UI Automation & Testing",
    link: "",
    summary:
      "Break free from brittle selectors. Moondream understands UI elements semantically, 'Locate the Submit button' or 'Is an error displayed?', making test automation resilient and maintainable.",
  },
];

const HorizontalCard = ({ video, Icon, title, link, summary }: card) => {
  const videoRef = useRef<HTMLVideoElement | null>(null);

  const linkBlock = link !== "" &&
    <div>
      <Button variant="outline" size="sm" asChild>
        <a href={link}>Learn more</a>
      </Button>
    </div>;

  return (
    <div
      className="border-border group flex flex-col gap-6 border-b py-8 last:border-b-0 md:flex-row md:items-center"
      onMouseEnter={() => {
        if (videoRef.current) {
          videoRef.current.play();
        }
      }}
      onMouseLeave={() => {
        if (videoRef.current) {
          videoRef.current.pause();
          videoRef.current.currentTime = 0;
        }
      }}
    >
      <div className="md:w-1/3">
        <AspectRatio ratio={16 / 9} className="overflow-hidden rounded-lg">
          <video
            src={video}
            className="block size-full object-cover object-center"
            ref={videoRef}
            controls={false}
            muted={true}
          />
        </AspectRatio>
      </div>

      <div className="flex flex-1 flex-col gap-4 md:pl-6">
        <div className="flex items-center gap-3">
          <div className="bg-primary flex size-8 rounded-md">
            <Icon className="text-primary-foreground m-auto size-4" />
          </div>
          <h3 className="text-xl font-semibold">{title}</h3>
        </div>

        <p className="text-muted-foreground leading-relaxed">{summary}</p>
        {linkBlock}
      </div>
    </div>
  );
};

const Solutions = () => {
  return (
    <PageContent className="pt-0 md:pt-1 pb-8 md:pb-10">
      <div>
        <SectionHeader
          sectionIcon={<CircleArrowRight className="size-4" />}
          section="Solutions"
          heading="Build Amazing Vision AI Products."
          description="From robotics to enterprise automation, Moondream powers the next generation of intelligent systems"
        />

        <div className="mx-auto max-w-4xl">
          {List.map((item, index) => (
            <HorizontalCard key={index} {...item} />
          ))}
        </div>
      </div>
    </PageContent>
  );
};

export { Solutions };
